/**
 * NLWorkflowInput - Natural language text input for workflow generation (T7.2)
 *
 * Text area + "Generate Workflow" button. Sends description + available agents
 * to the parse service.
 *
 * Patent: Natural Language Workflow Builder for AI-Governed Systems
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState } from 'react';
import { FiZap, FiAlertCircle } from 'react-icons/fi';
import { parseNaturalLanguage } from './nlParseService';
import type { ParsedWorkflow } from './types';

interface NLWorkflowInputProps {
  availableAgents: Array<{
    id: string;
    name: string;
    category: string;
    description?: string;
  }>;
  onWorkflowGenerated: (workflow: ParsedWorkflow, confidence: number) => void;
  onPrefill?: (prompt: string) => void;
}

const NLWorkflowInput: React.FC<NLWorkflowInputProps> = ({
  availableAgents,
  onWorkflowGenerated,
}) => {
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!description.trim()) return;
    setIsGenerating(true);
    setError(null);

    try {
      const result = await parseNaturalLanguage({
        description: description.trim(),
        availableAgents,
      });

      if (result.success && result.workflow.steps.length > 0) {
        onWorkflowGenerated(result.workflow, result.parseConfidence);
      } else {
        setError(
          result.workflow.warnings?.[0] ||
          'Could not parse workflow steps. Try describing each step more explicitly.'
        );
      }
    } catch (err) {
      setError('Failed to generate workflow. Please try again.');
      console.error('[NLWorkflowInput] Parse error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Describe your workflow in plain English
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={5}
          placeholder={`Example: "First, scan the codebase for security vulnerabilities. Then run compliance checks against our standards. If any critical issues are found, create an incident report and notify the security team. Finally, generate a summary report with remediation steps."`}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
          disabled={isGenerating}
        />
        <p className="mt-1 text-xs text-gray-500">
          Tip: Write each step as a separate sentence. Use agent names for better matching. Press {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to generate.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <FiAlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700">{error}</p>
        </div>
      )}

      <button
        onClick={handleGenerate}
        disabled={isGenerating || !description.trim() || availableAgents.length === 0}
        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
      >
        {isGenerating ? (
          <>
            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <FiZap className="h-4 w-4" />
            Generate Workflow
          </>
        )}
      </button>
    </div>
  );
};

export default NLWorkflowInput;
